import React from 'react';
import { SaveIcon } from '../../ui/SaveIcon';
import type { SavedViewsController } from '../../hooks/useSavedViews';

type LibrarySavedViewsProps<C> = {
  controller: SavedViewsController<C>;
};

/**
 * The Advanced panel's "Views" section: apply, default, save, update and remove.
 * Markup matches the Events library's original section so every library looks the same.
 */
export function LibrarySavedViews<C>({ controller }: LibrarySavedViewsProps<C>) {
  const {
    views,
    appliedId,
    appliedView,
    defaultViewId,
    hasChanges,
    canUpdate,
    applyView,
    saveView,
    updateView,
    removeView,
    setDefaultView,
  } = controller;

  const handleApplyChange = React.useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    applyView(event.target.value);
  }, [applyView]);
  const handleDefaultChange = React.useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setDefaultView(event.target.value || null);
  }, [setDefaultView]);
  const handleRemoveChange = React.useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    removeView(event.target.value);
    event.target.value = '';
  }, [removeView]);

  const updateTitle = appliedView
    ? hasChanges
      ? `Update ${appliedView.name} with the current filters`
      : 'Current filters match the selected saved view'
    : 'Apply a saved view before updating it';

  return (
    <div className="library-toolbar__advanced-section">
      <span className="library-toolbar__advanced-section-label text-xxs fg-muted">Views</span>
      <div className="library-toolbar__views-row">
        <label className="library-toolbar__field library-toolbar__field--compact text-xxs fg-muted">
          <span>Apply view</span>
          <select
            className="xs"
            value={appliedId ?? ''}
            onChange={handleApplyChange}
            aria-label="Apply saved view"
          >
            <option value="">Apply view…</option>
            {views.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </label>
        <label className="library-toolbar__field library-toolbar__field--compact text-xxs fg-muted">
          <span>Default</span>
          <select
            className="xs"
            value={defaultViewId ?? ''}
            onChange={handleDefaultChange}
            aria-label="Default view (loads on open)"
          >
            <option value="">None</option>
            {views.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn-save"
          onClick={saveView}
          title="Save current filters as a named view"
          aria-label="Save current view"
          style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
        >
          <span aria-hidden="true">+</span>
        </button>
        <button
          type="button"
          className="btn-save btn-save--view-update"
          onClick={updateView}
          disabled={!canUpdate}
          title={updateTitle}
          aria-label={appliedView ? `Update ${appliedView.name}` : 'Update current saved view'}
          style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
        >
          <SaveIcon />
        </button>
        {views.length > 0 && (
          <label className="library-toolbar__field library-toolbar__field--compact text-xxs fg-muted">
            <span>Remove</span>
            <select
              className="xs"
              value=""
              onChange={handleRemoveChange}
              aria-label="Remove saved view"
            >
              <option value="">Remove view…</option>
              {views.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </label>
        )}
      </div>
    </div>
  );
}

export default LibrarySavedViews;
